/**
 * CSS column-reverse 기반 RoomContent
 * 
 * 장점:
 * - 라이센스 불필요 (순수 CSS)
 * - prepend 시 스크롤 위치 자동 유지
 * - 구현 간단
 * 
 * 단점:
 * - 가상화 없음 → 수백 개 메시지 시 성능 저하
 * - DOM에 모든 메시지 렌더링
 */

import { Trans, t } from '@lingui/macro';
import { Loader2, RefreshCw, X, ChevronDown } from 'lucide-react';
import { memo, useCallback, useEffect, useReducer, useRef, useState } from 'react';
import { useAppUIContext } from '../../context/AppUIContext';
import { Message, SendFile } from '../../models/Message';
import { Room } from '../../models/Room';
import { Fire } from '../../services/FireClient';
import { FireClientMsg } from '../../services/FireClientMsg';
import { MessageLoader } from '../../services/MessageLoader';
import { My } from '../../services/My';
import { MyNotifs } from '../../services/MyNotifs';
import { UtilApp } from '../../services/Utils/UtilApp';
import { Util } from '../../services/Utils/Util';
import { UIMessage } from '../components/UIMessage';
import { MessageItemState } from '../components/MessageItem';
import MsgInputFooter from '../components/message-inputs/MsgInputFooter';
import { RoomRefreshEmitter, RoomViewState } from './RoomEvents';
import { RoomWelcomeMessage } from './RoomWelcomeMessage';
import { Network } from "@capacitor/network";
import { PushService } from '../../services/Utils/PushService';
import { Capacitor } from "@capacitor/core";
import { useMessageContext } from '../main/NotificationModal';
import { Notification } from "../../models/Notification";
import { UserSimplified } from "../../models/User";
import { useHistory } from '../../hooks/useRouterCompat';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '../../components/ui/avatar';
import { RoomMessageItem } from './RoomMessageItem';
import { appLifecycle } from '../../services/Utils/AppLifecycle';

interface Props {
  room: Room;
  onProgressChange?: (inProgress: boolean) => void;
}

const RoomContent: React.FC<Props> = ({ room, onProgressChange }) => {
  const history = useHistory();
  const [inProgress, setInProgress] = useState(false);
  const [state, setState] = useState<RoomViewState>('loading');
  const [currentMessage, setCurrentMessage] = useState<UIMessage | null>(null);
  const [noMoreMessages, setNoMoreMessages] = useState(false);
  const [messages, setMessages] = useState<UIMessage[]>([]);
  const [_, forceUpdate] = useReducer((x) => x + 1, 0);
  const { filter: type } = useAppUIContext();
  const [progressForFile, setProgressForFile] = useState<{ [key: string]: SendFile }>({});
  const messageRefs = useRef<{ [key: string]: HTMLDivElement | null }>({});
  const [sendingMessage, setSendingMessage] = useState<{ [key: string]: UIMessage }>({});
  const sendingMessageRef = useRef<{ [key: string]: UIMessage }>({}); // 🔧 ref로도 관리 (클로저 문제 해결)
  const [highlightedId, setHighlightedId] = useState<string | null>(null);
  const { notification, setNotification } = useMessageContext();
  const transitionRefs = useRef<Record<string, { current: HTMLDivElement | null }>>({});

  const messageLoader = useRef<MessageLoader | null>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const loadingMoreRef = useRef(false);
  const cooldownRef = useRef(false);
  const networkListenerRef = useRef<any>(null);
  const isInitiatingRef = useRef(false); // 🔧 초기화 중복 실행 방지 가드

  // 스레드 인라인 펼침 상태
  const [activeThreads, setActiveThreads] = useState<Set<string>>(new Set());
  const activeThreadsRef = useRef<Set<string>>(new Set()); // 🔧 ref로도 관리 (클로저 문제 해결)

  // 🔧 스레드 펼칠 때 원글을 화면 상단으로 스크롤
  const scrollToThreadTop = useCallback((id: string) => {
    // 렌더링 완료 후 스크롤 (스레드 확장 애니메이션 대기)
    setTimeout(() => {
      const element = messageRefs.current[id];
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 150);
  }, []);

  const toggleActiveThread = useCallback((id: string) => {
    console.log('[COLUMN-REVERSE] toggleActiveThread called:', id);

    // 🔧 ref 먼저 업데이트 (즉시 반영)
    const isOpening = !activeThreadsRef.current.has(id);

    // 스레드를 닫을 때: 부모 메시지의 현재 화면 위치 저장
    let messageElement: HTMLElement | null = null;
    let messageTopBeforeClose = 0;

    if (!isOpening && scrollContainerRef.current) {
      // 메시지 요소 찾기
      messageElement = scrollContainerRef.current.querySelector(`[data-message-id="${id}"]`) as HTMLElement;
      if (messageElement) {
        // 뷰포트 기준 현재 위치 저장
        messageTopBeforeClose = messageElement.getBoundingClientRect().top;
      }
    }

    if (activeThreadsRef.current.has(id)) {
      activeThreadsRef.current.delete(id);
    } else {
      activeThreadsRef.current.add(id);
    }

    console.log('[COLUMN-REVERSE] Thread state after update:', { id, isOpening, current: [...activeThreadsRef.current] });

    // state도 업데이트 (리렌더링 트리거)
    setActiveThreads(new Set(activeThreadsRef.current));

    // 스레드를 열 때만 스크롤
    if (isOpening) {
      scrollToThreadTop(id);
    } else if (messageElement && scrollContainerRef.current) {
      // 스레드를 닫을 때: 메시지의 화면 위치 유지
      requestAnimationFrame(() => {
        if (messageElement && scrollContainerRef.current) {
          const messageTopAfterClose = messageElement.getBoundingClientRect().top;
          const scrollDelta = messageTopAfterClose - messageTopBeforeClose;

          // column-reverse에서는 scrollTop이 음수 방향이므로 반대로 조정
          scrollContainerRef.current.scrollTop += scrollDelta;
        }
      });
    }
  }, [scrollToThreadTop]);

  const addActiveThread = useCallback((id: string) => {
    const isOpening = !activeThreadsRef.current.has(id);

    // ref 먼저 업데이트
    activeThreadsRef.current.add(id);

    // state도 업데이트
    setActiveThreads(new Set(activeThreadsRef.current));

    // 스레드를 열 때만 스크롤
    if (isOpening) {
      scrollToThreadTop(id);
    }
  }, [scrollToThreadTop]);

  const [mentionTarget, setMentionTarget] = useState<{ user: UserSimplified, messageId: string, threadId: string } | null>(null);

  // 토스트 큐
  const [toastQueue, setToastQueue] = useState<Message[]>([]);
  const shownToastIdsRef = useRef<Set<string>>(new Set());
  const threadToast = toastQueue[0] || null;
  const addToast = (m: Message) => {
    if (shownToastIdsRef.current.has(m.id)) return;
    shownToastIdsRef.current.add(m.id);
    setToastQueue(prev => [...prev, m]);
  };
  const dismissToast = () => setToastQueue(prev => prev.slice(1));
  const [highlightedReaction, setHighlightedReaction] = useState<{ messageId: string; emoticon: string } | null>(null);

  // inProgress 변경 시 부모에게 알림
  useEffect(() => {
    onProgressChange?.(inProgress);
  }, [inProgress, onProgressChange]);

  // 알림 리셋 (onNewMessages에서 사용하기 위해 먼저 정의)
  // ref로 최신 함수 유지 (onNewMessages 의존성 체인 방지)
  const resetNotificationsForRoomRef = useRef<() => Promise<void>>();

  const resetNotificationsForRoom = useCallback(async () => {
    const isFg = await UtilApp.isForeground();
    console.log('[NOTIF] resetNotificationsForRoom called, isForeground:', isFg, 'roomId:', room.id);

    if (isFg) {
      setTimeout(() => {
        if (Capacitor.isNativePlatform()) {
          PushService.removeNotificationsByRoomId(room.id);
        }

        const allNotifs = MyNotifs.NotificationsByRoom.get(room.id);
        console.log('[NOTIF] All notifications for room:', allNotifs?.length ?? 0);

        let notifs = allNotifs?.filter(n => n.isNew && n.type != 'Voice' && n.type != 'CallStart');
        console.log('[NOTIF] New notifications (excluding Voice/Call):', notifs?.length ?? 0);

        if (notifs && notifs.length > 0) {
          const allMsgIds = messageLoader.current?.allMessages.map(m => m.id) ?? [];
          console.log('[NOTIF] Loaded message IDs count:', allMsgIds.length);

          notifs = notifs.filter(notif => {
            if (notif.isNew) {
              const msgs = messageLoader.current?.allMessages.filter(m => m.id == notif.messageId) ?? [];
              const found = msgs.length > 0;
              if (!found) {
                console.log('[NOTIF] Message NOT found for notification:', notif.messageId?.substring(0, 10));
              }
              return found;
            }
            return false;
          });

          console.log('[NOTIF] Notifications to clear:', notifs.length);
          if (notifs.length > 0) {
            Fire.clearNotifications(notifs);
          }
        }
      }, 150);
    }
  }, [room.id]);

  // ref에 최신 함수 저장
  useEffect(() => {
    resetNotificationsForRoomRef.current = resetNotificationsForRoom;
  }, [resetNotificationsForRoom]);

  // 새 메시지 콜백 - 기존 RoomContent 로직 적용
  // 🔧 sendingMessageRef.current를 사용하여 항상 최신 값 접근 (클로저 문제 해결)
  const onNewMessages = useCallback((_messages: UIMessage[], newParentMessages: boolean, visibleMsgs: Message[], invisibleMsgs: Message[]) => {
    const currentSendingMessage = sendingMessageRef.current;



    // sendingMessage 정리 - 서버에서 확인된 메시지는 sending 상태 제거
    _messages.forEach(m => {
      if (currentSendingMessage[m.msg.id]) {

        delete sendingMessageRef.current[m.msg.id];
        setSendingMessage((prev) => {
          const { [m.msg.id]: _, ...rest } = prev;
          return rest;
        });
      }
      // 자식 메시지도 체크
      m.children.forEach(c => {
        if (currentSendingMessage[c.msg.id]) {

          delete sendingMessageRef.current[c.msg.id];
          setSendingMessage((prev) => {
            const { [c.msg.id]: _, ...rest } = prev;
            return rest;
          });
        }
      });
    });

    // 불변성 유지: 새 배열로 sendingMessage 병합
    let updatedMessages = [..._messages];

    // 🔧 _messages 내의 isLocal=true 메시지들을 isLocal=false로 변경
    // (서버에서 메시지가 도착했으므로 로컬 상태 해제)
    updatedMessages = updatedMessages.map(m => {
      let needsUpdate = false;

      // 부모 메시지 확인
      if (m.isLocal) {
        m.isLocal = false;
        needsUpdate = true;

      }

      // 자식 메시지 확인
      const updatedChildren = m.children.map(c => {
        if (c.isLocal) {
          c.isLocal = false;
          needsUpdate = true;

        }
        return c;
      });

      if (needsUpdate) {
        return m.withChildren(updatedChildren);
      }
      return m;
    });

    // 🔧 ref에서 최신 값 읽기
    Object.keys(sendingMessageRef.current).forEach(key => {
      const uimsg = sendingMessageRef.current[key];
      if (uimsg.msg.roomId === room.id) {
        const m = uimsg.msg;

        // 이미 존재하는지 확인
        const existsInParent = updatedMessages.some(msg => msg.msg.id === m.id);
        const existsInChildren = updatedMessages.some(msg =>
          msg.children.some(c => c.msg.id === m.id)
        );

        if (existsInParent || existsInChildren) {
          return; // 이미 존재하면 스킵
        }

        if (m.parentMessageId) {
          // 부모를 새 객체로 교체 (불변성 유지)
          updatedMessages = updatedMessages.map(parentMsg => {
            if (parentMsg.msg.id === m.parentMessageId) {
              return parentMsg.withChild(uimsg);
            }
            return parentMsg;
          });
        } else {
          updatedMessages = [...updatedMessages, uimsg];
        }
      }
    });

    // 🔧 Toast for invisible messages (early return 전에 먼저 처리!)
    invisibleMsgs.forEach(m => {
      if (m.parentMessageId && m.user.id !== My.user.id) {
        addToast(m);
      }
    });

    // visibleMsgs가 없고 sendingMessage도 없으면 업데이트 스킵
    if (visibleMsgs.length === 0 && Object.keys(sendingMessageRef.current).length === 0) {
      return;
    }

    setMessages(updatedMessages);

    // 🔧 visibleMsgs 중 스레드가 펼쳐져 있지 않은 댓글도 토스트 표시
    visibleMsgs.forEach(m => {
      if (m.parentMessageId && m.user.id !== My.user.id) {
        // 스레드가 펼쳐져 있지 않으면 토스트 표시
        if (!activeThreadsRef.current.has(m.parentMessageId)) {
          addToast(m);
        }
      }
    });

    // 🔧 하단으로 스크롤 (새 메시지가 도착하고 하단에 있을 때)
    if (newParentMessages && scrollContainerRef.current) {
      // column-reverse에서는 scrollTop = 0이 하단
      scrollContainerRef.current.scrollTop = 0;
    }

    // 🔧 새 메시지 도착 시 알림 자동 클리어 (현재 방을 보고 있으므로)
    if (visibleMsgs.length > 0 || invisibleMsgs.length > 0) {
      // 비동기로 알림 클리어 (UI 렌더링 우선)
      setTimeout(() => {
        resetNotificationsForRoomRef.current?.();
      }, 100);
    }
  }, [room.id]); // resetNotificationsForRoom을 ref로 접근하므로 의존성 제거

  // 초기화
  const initiate = useCallback(async () => {
    if (isInitiatingRef.current) {
      console.log('[ROOM] initiate already in progress, skipping...');
      return;
    }

    console.log('[ROOM] initiate started for room:', room.id);
    isInitiatingRef.current = true;
    setState('loading');
    setNoMoreMessages(false);
    setMessages([]);
    setHighlightedId(null);
    setActiveThreads(new Set());

    try {
      messageLoader.current?.unsubscribe();
      messageLoader.current = new MessageLoader(room.id, onNewMessages);

      const limitCount = Math.ceil(window.innerHeight / 80) + 2;
      await messageLoader.current.loadOlderMessageAndSubscribeToNew(null, limitCount);

      const uimsgs = messageLoader.current.getUIMessages();
      setMessages(uimsgs);

      setState('ready');
      // 🔧 ref를 통해 최신 함수 호출 (의존성 체인 방지)
      resetNotificationsForRoomRef.current?.();
    } catch (error) {
      console.error('[ROOM] initiate failed:', error);
      setState('error');
    } finally {
      isInitiatingRef.current = false;
      console.log('[ROOM] initiate finished for room:', room.id);
    }
  }, [room.id, onNewMessages]); // resetNotificationsForRoom은 ref로 접근하므로 의존성 제거

  // Room 변경 또는 마운트 시 초기화
  useEffect(() => {
    initiate();

    // 메시지 삭제 등의 이벤트 발생 시 messages 상태 재계산
    const handleRoomRefresh = () => {
      setMessages(prev => prev
        // 삭제된 부모 메시지 필터링
        .filter(m => !My.DeletedMessages.get(m.msg.id))
        .map(m => {
          // 삭제된 자식 메시지(댓글)도 필터링
          const filteredChildren = m.children.filter(c => !My.DeletedMessages.get(c.msg.id));
          if (filteredChildren.length !== m.children.length) {
            return m.withChildren(filteredChildren);
          }
          return m;
        })
      );
    };

    RoomRefreshEmitter.on(room.id, handleRoomRefresh);

    // Network listener
    if (Capacitor.isNativePlatform()) {
      const setupNetworkListener = async () => {
        if (networkListenerRef.current) {
          networkListenerRef.current.remove();
        }

        // 🔧 마지막 연결 상태를 추적하여 불필요한 initiate 방지
        let lastConnected = true;

        networkListenerRef.current = await Network.addListener('networkStatusChange', (status) => {
          console.log('[NETWORK] statusChange:', status.connected, 'lastConnected:', lastConnected);

          // 1. 끊겼다가 다시 연결된 경우에만 initiate 실행
          // 2. 현재 이미 로딩 중인 경우에는 무시
          if (status.connected && !lastConnected) {
            console.log('[NETWORK] Reconnected. Initiating room content...');
            initiate();
          }

          lastConnected = status.connected;
        });
      };
      setupNetworkListener();
    }

    // 🔧 포그라운드 복귀 시 해당 방의 알림 자동 클리어
    // 같은 방에 있다가 홈 → 푸시 통지 터치로 돌아올 때 room.id가 변하지 않아
    // initiate()가 재실행되지 않는 문제를 해결
    const unsubLifecycle = appLifecycle.onStateChange((isBackground) => {
      if (!isBackground) {
        console.log('[ROOM] Foreground recovered, clearing notifications for room:', room.id);
        setTimeout(() => {
          resetNotificationsForRoomRef.current?.();
        }, 300);
      }
    });

    return () => {
      messageLoader.current?.unsubscribe();
      RoomRefreshEmitter.off(room.id, handleRoomRefresh);
      unsubLifecycle();
      if (networkListenerRef.current) {
        networkListenerRef.current.remove();
      }
    };
  }, [room.id, initiate]);

  // 이전 메시지 로드 (loadmore)
  const loadOlderMessages = useCallback(async () => {
    if (loadingMoreRef.current || cooldownRef.current || noMoreMessages || !messageLoader.current) return;

    loadingMoreRef.current = true;
    setInProgress(true);

    console.log(`📥 [LOADMORE] 이전 메시지 로드 시작, limit: ${FireClientMsg.NrMsgsPerLoad}`);
    const nrOlderCount = await messageLoader.current.loadOlderMessageAndSubscribeToNew(
      null,
      FireClientMsg.NrMsgsPerLoad
    );

    console.log(`📥 [LOADMORE] 로드된 메시지 수: ${nrOlderCount}, noMoreMessages 설정: ${nrOlderCount < FireClientMsg.NrMsgsPerLoad}`);
    setNoMoreMessages(nrOlderCount < FireClientMsg.NrMsgsPerLoad);

    const uimsgs = messageLoader.current.getUIMessages();
    // 🔧 핵심: column-reverse에서는 그냥 setMessages만 해도
    // 스크롤 위치가 자동으로 유지됨!
    setMessages(uimsgs);
    console.log(`📥 [LOADMORE] Total: ${uimsgs.length} messages`);

    loadingMoreRef.current = false;
    setInProgress(false);

    // 쿨다운
    cooldownRef.current = true;
    setTimeout(() => {
      cooldownRef.current = false;
    }, 500);
  }, [noMoreMessages]);

  // 스크롤 이벤트 핸들러
  const handleScroll = useCallback((e: React.UIEvent<HTMLDivElement>) => {
    const container = e.currentTarget;
    // column-reverse에서는 scrollTop이 음수 또는 0에 가까울 때가 "상단"
    // 실제로는 가장 오래된 메시지가 있는 위치
    const scrollBottom = container.scrollHeight + container.scrollTop - container.clientHeight;

    // 상단(오래된 메시지)에 가까우면 loadmore
    if (scrollBottom < 100 && !loadingMoreRef.current && !cooldownRef.current && !noMoreMessages && state === 'ready') {
      loadOlderMessages();
    }
  }, [loadOlderMessages, noMoreMessages, state]);



  // NodeRef 관리
  const getNodeRef = useCallback((id: string) => {
    if (!transitionRefs.current[id]) {
      transitionRefs.current[id] = { current: null };
    }
    return transitionRefs.current[id];
  }, []);

  // 파일 진행률
  const onProgressForFile = useCallback((key: string, sendFile: SendFile) => {
    setProgressForFile(prev => ({ ...prev, [key]: sendFile }));
  }, []);

  // 메시지 전송 완료 - sending 상태 제거 및 isLocal 업데이트
  const onSendedMessage = useCallback((uimsg: UIMessage) => {
    // ref도 함께 업데이트
    delete sendingMessageRef.current[uimsg.msg.id];
    setSendingMessage((prev) => {
      const newMsg = { ...prev };
      delete newMsg[uimsg.msg.id];
      return newMsg;
    });

    // 🔧 messages 상태에서 해당 메시지의 isLocal을 false로 업데이트
    setMessages((prev) => {
      const msgId = uimsg.msg.id;
      const parentId = uimsg.msg.parentMessageId;

      if (parentId) {
        // 자식 메시지(답글): 부모를 찾아서 해당 자식의 isLocal 업데이트
        return prev.map((parentMsg) => {
          if (parentMsg.msg.id === parentId) {
            const updatedChildren = parentMsg.children.map((child) => {
              if (child.msg.id === msgId) {
                child.isLocal = false;
              }
              return child;
            });
            // 🔧 FIX: spread 연산자 대신 withChildren 사용하여 클래스 메서드 유지
            return parentMsg.withChildren(updatedChildren);
          }
          return parentMsg;
        });
      } else {
        // 부모 메시지: 직접 isLocal 업데이트
        return prev.map((msg) => {
          if (msg.msg.id === msgId) {
            msg.isLocal = false;
          }
          return msg;
        });
      }
    });
  }, []);

  // 불변성을 유지하면서 로컬 메시지를 추가한 새 배열 반환
  const pushLocalMessage = useCallback((currentMessages: UIMessage[], uimsg: UIMessage): UIMessage[] => {
    const m = uimsg.msg;
    if (m.parentMessageId) {
      // 자식 메시지(답글): 부모를 찾아서 자식 추가
      return currentMessages.map(parentMsg => {
        if (parentMsg.msg.id === m.parentMessageId) {
          return parentMsg.withChild(uimsg);
        }
        return parentMsg;
      });
    } else {
      // 부모 메시지: 새 배열에 추가
      return [...currentMessages, uimsg];
    }
  }, []);

  // 하단으로 스크롤
  const scrollViewToBottom = useCallback(() => {
    if (scrollContainerRef.current) {
      // column-reverse에서는 scrollTop = 0이 하단
      scrollContainerRef.current.scrollTop = 0;
    }
  }, []);

  // 로컬 메시지 추가 (sending 상태 포함)
  const addLocalMsg = useCallback((uimsg?: UIMessage) => {
    setCurrentMessage(null);
    if (!uimsg) return;

    try {
      const m = uimsg.msg;
      // sending 상태 추가 (ref와 state 둘 다)
      sendingMessageRef.current[m.id] = uimsg;
      setSendingMessage((prev) => ({ ...prev, [m.id]: uimsg }));
      // 메시지 추가 (답글이면 부모에, 아니면 배열 끝에)
      setMessages(prevMessages => pushLocalMessage(prevMessages, uimsg));

      if (!m.parentMessageId) {
        // 부모 메시지: 하단으로 스크롤
        scrollViewToBottom();
      } else {
        // 🔧 답글 전송 시 해당 스레드 자동 펼침
        addActiveThread(m.parentMessageId);
      }
    } catch (error) {
      console.error('Failed to add local message', error);
    }
  }, [pushLocalMessage, scrollViewToBottom, addActiveThread]);

  // 자식 메시지 로드 - messages state도 업데이트
  const handleLoadChildren = useCallback((parentId: string, children: Message[]) => {
    // Keep MessageLoader state in sync
    messageLoader.current?.ingestLoadedChildren(parentId, children);

    // 🔍 디버그: 로드된 children 데이터 확인
    console.log('📦 handleLoadChildren:', {
      parentId,
      totalChildren: children.length,
      children: children.map(c => ({
        id: c.id,
        text: c.text?.substring(0, 30),
        emoticon: c.emoticon,
        replyToId: c.replyTo?.id,
        parentMessageId: c.parentMessageId
      }))
    });

    // Optimistically update UI with immutable merge
    setMessages((prev) => {
      // 1단계: 일반 답글/대댓글 (emoticon이 없는 것) - 모두 같은 레벨에 표시
      const replies = children.filter(c => !c.emoticon);

      // 2단계: 리액션 (emoticon이 있는 것) - 각 메시지의 children으로 분류
      const reactions = children.filter(c => c.emoticon);

      console.log('📊 분류 결과:', { replies: replies.length, reactions: reactions.length });

      // 3단계: 리액션을 replyToMessageId 기준으로 그룹화
      const reactionsByReplyTo = new Map<string, Message[]>();
      reactions.forEach(r => {
        const replyToMsgId = r.replyToMessageId;
        if (replyToMsgId) {
          const existing = reactionsByReplyTo.get(replyToMsgId) || [];
          existing.push(r);
          reactionsByReplyTo.set(replyToMsgId, existing);
        }
      });

      console.log('🔗 리액션 그룹화:', Object.fromEntries(reactionsByReplyTo));

      // 4단계: 답글을 UIMessage로 변환하면서 해당 답글의 리액션 포함
      const incoming = replies.map((c) => {
        const myReactions = reactionsByReplyTo.get(c.id) || [];
        console.log(`📝 답글 ${c.id.substring(0, 8)}... 에 리액션 ${myReactions.length}개`);
        return new UIMessage(c, myReactions.map(r => new UIMessage(r, [])));
      });

      return prev.map((m) => {
        if (m.msg.id !== parentId) return m;

        // Dedupe by id and merge reactions
        const map = new Map<string, UIMessage>();
        m.children.forEach((c) => map.set(c.msg.id, c));
        incoming.forEach((c) => {
          const existing = map.get(c.msg.id);
          if (existing) {
            // 기존 reactions와 새로운 reactions 병합
            const mergedReactions = [...existing.children];
            c.children.forEach(r => {
              if (!mergedReactions.find(e => e.msg.id === r.msg.id)) {
                mergedReactions.push(r);
              }
            });
            const merged = new UIMessage(c.msg, mergedReactions);
            map.set(c.msg.id, merged);
          } else {
            map.set(c.msg.id, c);
          }
        });

        // 시간순 정렬
        const merged = Array.from(map.values()).sort((a, b) => {
          const aTime = new Date(a.msg.created).getTime();
          const bTime = new Date(b.msg.created).getTime();
          return aTime - bTime;
        });

        return m.withChildren(merged);
      });
    });
  }, []);

  // 메시지 클릭
  const messageClicked = useCallback((uimsg: UIMessage) => {
    console.log("🔧 messageClicked (ColumnReverse)", uimsg.msg.id, uimsg.msg.text?.substring(0, 30));
    setCurrentMessage(uimsg);

    // 멘션 대상 설정 (스레드 ID 포함)
    setMentionTarget({
      user: uimsg.msg.user,
      messageId: uimsg.msg.id,
      threadId: uimsg.msg.parentMessageId ?? uimsg.msg.id
    });
  }, []);

  // 메시지 상태 - sendingMessage 포함
  const getMessageItemState = useCallback((m: UIMessage): MessageItemState => {
    // sending 상태 체크
    if (sendingMessage[m.msg.id]) {
      return 'new'; // sending 상태는 'new'로 표시 (스피너는 RoomMessageItem에서 처리)
    }
    if (m.msg.inProgress) {
      return 'new';
    }

    let itemState: MessageItemState = 'new';
    if (currentMessage?.msg.id === m.msg.id && state === 'playing') {
      itemState = 'playing';
    } else {
      itemState = m.isNew() ? 'new' : 'old';
    }
    return itemState;
  }, [currentMessage, state, sendingMessage]);

  // 재전송
  const resendMessage = useCallback((uimsg: UIMessage) => {
    // TODO: 구현
  }, []);

  // 필터
  const shouldShow = useCallback((msg: Message) => {
    if (type === 'photos') return msg.files?.length > 0 && msg.files[0].group === 'image';
    if (type === 'docs') return msg.files?.length > 0 && msg.files[0].group === 'doc';
    if (type === 'voice') return !!msg.recording;
    if (type === 'video') return !!msg.videoUrl;
    return true;
  }, [type]);

  // 타겟 메시지 - 메시지가 없으면 API로 로드 후 스크롤
  const targetMessage = useCallback(async (messageId: string | null, parentMessageId: string | null) => {
    if (!messageId) return;

    console.log("[COLUMN-REVERSE] targetMessage:", messageId, "parent:", parentMessageId);

    // 1. 이미 로드된 메시지인지 확인
    const notifMsg = messageLoader.current?.allMessages.find(m => m.id == messageId);

    if (notifMsg) {
      console.log("[COLUMN-REVERSE] Message found in memory");
      // 자식 메시지인 경우 스레드 펼치기
      if (notifMsg.parentMessageId) {
        addActiveThread(notifMsg.parentMessageId);
      }

      setHighlightedId(messageId);
      setTimeout(() => setHighlightedId(null), 3000);

      // DOM 스크롤 (재시도 로직 포함)
      const scrollToMessage = (retries = 5) => {
        const element = messageRefs.current[messageId];
        if (element) {
          console.log('[COLUMN-REVERSE] Scrolling to message (memory):', messageId);
          element.scrollIntoView({ behavior: 'smooth', block: 'center' });
        } else if (retries > 0) {
          console.log('[COLUMN-REVERSE] Element not found (memory), retrying...', retries);
          setTimeout(() => scrollToMessage(retries - 1), 200);
        } else {
          console.warn('[COLUMN-REVERSE] Could not find element after retries (memory):', messageId);
        }
      };
      // 스레드 펼침 후 DOM 렌더링 대기
      setTimeout(() => scrollToMessage(), 300);
      return;
    }

    // 2. 메시지가 없으면 API로 로드
    console.log("[COLUMN-REVERSE] Message NOT in memory. Loading from API...");
    setInProgress(true);

    try {
      const msg = await Fire.msg.getMessage(messageId);
      if (!msg) {
        console.warn("[COLUMN-REVERSE] Message not found in API:", messageId);
        setInProgress(false);
        return;
      }

      let parentId = msg.parentMessageId || msg.id;
      let parentMsg = msg.parentMessageId
        ? await Fire.msg.getMessage(msg.parentMessageId)
        : msg;

      if (!parentMsg) {
        console.warn("[COLUMN-REVERSE] Parent message not found");
        setInProgress(false);
        return;
      }

      // 부모의 자식 메시지들 로드
      if (msg.parentMessageId) {
        const children = await Fire.msg.getChildrenMessages(My.user.id, parentId);
        messageLoader.current?.ingestLoadedChildren(parentId, children);
      }

      // 🔧 부모 메시지를 먼저 명시적으로 추가 (getMessagesAfter/getMessages는 해당 시간 미포함)
      console.log('[COLUMN-REVERSE] Adding parent message explicitly:', parentId);
      await messageLoader.current?.ingestMessages([parentMsg]);

      // 부모 이후의 메시지들도 로드 (최근 메시지 컨텍스트)
      const afterMsgs = await Fire.msg.getMessagesAfter(My.user.id, room.id, new Date(parentMsg.created));
      console.log('[COLUMN-REVERSE] Messages after parent:', afterMsgs.length);
      if (afterMsgs.length > 0 && messageLoader.current) {
        await messageLoader.current.ingestMessages(afterMsgs);
      }

      // 부모 이전의 메시지들 로드
      const olderMsgs = await Fire.msg.getMessages(My.user.id, room.id, new Date(parentMsg.created), FireClientMsg.NrMsgsPerLoad);
      console.log('[COLUMN-REVERSE] Messages before parent:', olderMsgs.length);
      if (olderMsgs.length > 0 && messageLoader.current) {
        await messageLoader.current.ingestMessages(olderMsgs);
      }

      // UI 갱신
      const uimsgs = messageLoader.current?.getUIMessages() ?? [];
      setMessages(uimsgs);

      // 스레드 펼침
      if (msg.parentMessageId) {
        addActiveThread(msg.parentMessageId);
      }

      // 하이라이트 및 스크롤
      setHighlightedId(messageId);
      setTimeout(() => setHighlightedId(null), 3000);

      // 렌더링 완료 후 스크롤 (재시도 로직 강화)
      const scrollToMessage = (retries = 5) => {
        const element = messageRefs.current[messageId];
        if (element) {
          console.log('[COLUMN-REVERSE] Scrolling to message:', messageId);
          element.scrollIntoView({ behavior: 'smooth', block: 'center' });
        } else if (retries > 0) {
          console.log('[COLUMN-REVERSE] Element not found, retrying...', retries);
          setTimeout(() => scrollToMessage(retries - 1), 200);
        } else {
          console.warn('[COLUMN-REVERSE] Could not find element after retries:', messageId);
        }
      };
      // 스레드 펼침 후 DOM 렌더링 시간 확보 (400ms 대기)
      setTimeout(() => scrollToMessage(), 400);

    } catch (error) {
      console.error("[COLUMN-REVERSE] Error loading message:", error);
    } finally {
      setInProgress(false);
    }
  }, [room.id, addActiveThread]);

  // 알림 처리 - 리액션 하이라이트 포함
  useEffect(() => {
    if (notification && state === 'ready') {
      const messageId = notification.type === 'Emoticon'
        ? notification.replyToMessageId
        : notification.messageId;

      // 리액션 알림인 경우 해당 이모지 하이라이트
      const notifAny = notification as any;
      if (notification.type === 'Emoticon' && notifAny.emoticon && notification.replyToMessageId) {
        setHighlightedReaction({
          messageId: notification.replyToMessageId,
          emoticon: notifAny.emoticon
        });
        setTimeout(() => setHighlightedReaction(null), 3000);
      }

      targetMessage(messageId, notification.parentMessageId ?? null);

      // 🔧 알림 클릭 후 해당 알림 읽음 처리
      setTimeout(() => {
        resetNotificationsForRoomRef.current?.();
      }, 500);

      setNotification(null);
    }
  }, [notification, state, targetMessage, setNotification]);

  // 필터된 메시지
  const filteredMessages = messages.filter(m => shouldShow(m.msg));

  // 로딩 상태
  if (state === 'loading') {
    return (
      <div className="flex-1 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="flex-1 flex flex-col min-h-0">
      {/* 메시지 리스트 - column-reverse 핵심! */}
      <div
        ref={scrollContainerRef}
        className="flex-1 overflow-y-auto overflow-x-hidden"
        onScroll={handleScroll}
        style={{
          display: 'flex',
          flexDirection: 'column-reverse',  // 🔧 핵심!
          overscrollBehavior: 'none'
        }}
      >
        {/* column-reverse에서는 DOM 순서가 역순으로 표시됨 */}
        {/* 따라서 최신 메시지(배열 끝)가 화면 하단에 표시 */}

        {messages.length === 0 && !inProgress ? (
          <RoomWelcomeMessage room={room} />
        ) : (
          <>
            {/* 
                         * column-reverse에서 DOM 순서가 역순으로 표시됨:
                         * - DOM 첫 번째 → 화면 하단
                         * - DOM 마지막 → 화면 상단
                         * 
                         * 메시지 배열은 [오래된...최신] 순서이므로
                         * 역순으로 렌더링해야 최신이 하단에, 오래된게 상단에 표시됨
                         */}
            {[...filteredMessages].reverse().map((message) => (
              <div
                key={`${message.msg.id}-${message.isLocal ? 'local' : 'server'}`}
                ref={(el) => messageRefs.current[message.msg.id] = el}
              >
                <RoomMessageItem
                  message={message}
                  room={room}
                  activeThreads={activeThreads}
                  toggleActiveThread={toggleActiveThread}
                  highlightedId={highlightedId}
                  highlightedReaction={highlightedReaction}
                  currentMessage={currentMessage}
                  messageRefs={messageRefs}
                  getNodeRef={getNodeRef}
                  getMessageItemState={getMessageItemState}
                  onMessageClick={messageClicked}
                  progressForFile={progressForFile}
                  onResendMessage={resendMessage}
                  mentionTarget={mentionTarget}
                  setMentionTarget={setMentionTarget}
                  forceUpdate={forceUpdate}
                  onTargetMessage={targetMessage}
                  onLoadChildren={handleLoadChildren}
                />
              </div>
            ))}

            {/* 로딩 인디케이터 / 더 불러오기 (column-reverse에서 DOM 마지막 = 화면 상단) */}
            <div className="flex justify-center py-4">
              {inProgress ? (
                <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
              ) : noMoreMessages ? (
                <span className="text-gray-400 text-sm"><Trans>No more messages</Trans></span>
              ) : (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={loadOlderMessages}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <Trans>Load more</Trans>
                </Button>
              )}
            </div>
          </>
        )}
      </div>

      {/* 메시지 입력 - flex 레이아웃 */}
      {type === 'none' && !Room.isArchivedOneToOne(room) && (
        <div className="flex-shrink-0 relative bg-white z-10 md:px-2 md:py-2 w-full border-t border-[#E5E5E5] pb-safe">
          {/* Thread Toast */}
          {threadToast && (
            <div
              className="absolute bottom-full left-4 right-4 mb-2 px-3 py-2 bg-white rounded-xl shadow-lg border border-gray-100 flex items-center gap-2 cursor-pointer hover:bg-gray-50 transition-colors"
              onClick={() => {
                console.log('[TOAST] Clicked. messageId:', threadToast.id, 'parentMessageId:', threadToast.parentMessageId);
                targetMessage(threadToast.id, threadToast.parentMessageId);
                dismissToast();
              }}
            >
              {threadToast.emoticon ? (
                <>
                  <img src={`/assets/icon/ic-${threadToast.emoticon}-24.svg`} alt={threadToast.emoticon} className="w-8 h-8" />
                  <div className="flex flex-col flex-1 min-w-0">
                    <span className="text-[10px] font-bold text-orange-500 line-clamp-1">
                      <Trans>New reaction</Trans>
                    </span>
                    <span className="text-[11px] text-gray-600 line-clamp-1">
                      {threadToast.user.name}
                    </span>
                  </div>
                </>
              ) : (
                <>
                  <Avatar className="w-6 h-6 flex-shrink-0">
                    <AvatarImage src={threadToast.user.photoUrl || undefined} />
                    <AvatarFallback className="text-[10px] bg-orange-100 text-orange-600">{threadToast.user.name?.[0]}</AvatarFallback>
                  </Avatar>
                  <div className="flex flex-col flex-1 min-w-0">
                    <span className="text-[10px] font-bold text-orange-500 line-clamp-1">
                      <Trans>New reply</Trans>: {threadToast.user.name}
                    </span>
                    <span className="text-[11px] text-gray-600 line-clamp-1">
                      {Util.getTextAsPlain(threadToast.text)}
                    </span>
                  </div>
                </>
              )}
              <Button variant="ghost" size="icon" className="h-6 w-6 rounded-full flex-shrink-0 ml-auto" onClick={(e) => { e.stopPropagation(); dismissToast(); }}>
                <X className="w-3 h-3" />
              </Button>
            </div>
          )}

          <MsgInputFooter
            room={room}
            parentMessage={currentMessage}
            messages={messages}
            onClearParent={(m) => { addLocalMsg(m); setCurrentMessage(null); }}
            onScrollToParent={() => {
              if (currentMessage) {
                targetMessage(currentMessage.msg.id, currentMessage.msg.parentMessageId ?? null);
              }
            }}
            onProgressForFile={(key, sendFile) => onProgressForFile(key, sendFile)}
            onSendedMessage={(m) => onSendedMessage(m)}
          />
        </div>
      )}
    </div>
  );
};

export default memo(RoomContent);
